import dotProp from 'dot-prop-immutable';
import sortBy from 'lodash/sortBy';
import { processNominatedOrReviewTicket, processAssignedTicket } from '../util/dataObjectToStoreObject';
import {
  ADD_PREVIOUS_TICKETS,
  ADD_TICKET,
  HYDRATE_ALL_TICKETS,
  HYDRATE_ASSIGNED_TICKETS,
  HYDRATE_HISTORICAL_TICKETS,
  HYDRATE_NOMINATED_TICKETS,
  MOVE_TICKET,
  MOVE_TICKET_FROM_ALL_TO_ASSIGNED,
  REMOVE_REVIEW_TICKET,
  REMOVE_TICKET,
  SELECT_ASSIGNED_TICKET,
  SELECT_LIVE_CHAT_TICKET,
  TICKETS_SET_TO_EMPTY,
  UPDATE_AWAITING_WEB_CHAT_NOMINATION_COUNT,
  UPDATE_AWAITING_EMAIL_MESSAGE_NOMINATION_COUNT,
  UPDATE_TICKET,
  UPDATE_TICKET_ORDER,
  HYDRATE_REVIEW_TICKETS,
  UPDATE_REVIEW_TICKET,
} from '../actions/types';
import { useFifoOrderingForTab, isTabFull, getSortParamsForTab, isTicketBeingViewed } from '../extractFromState';
import { CONVERSATION_QUERY_LIMIT } from '../constants/limits';
import { NOMINATED, ASSIGNED, LIVE_CHAT, ALL, REVIEW } from '../constants/tabNames';

export const extractTicketsArray = (queue) => queue.allTickets.map((id) => queue[id]);

const sortTickets = (ticketsArray, timeSortField, useFifoOrder) => {
  ticketsArray = sortBy(ticketsArray, (ticket) => ticket[timeSortField]);
  if (useFifoOrder) {
    return ticketsArray;
  }

  return ticketsArray.reverse();
};

const isStaleTicketUpdate = (newTicket, currentTicket) => {
  const currentTicketUpdatedAt = currentTicket?.updatedAt;
  const newTicketUpdatedAt = newTicket?.updatedAt;

  const currentTicketUpdatedAtDate = new Date(currentTicketUpdatedAt);
  const newTicketUpdatedAtDate = new Date(newTicketUpdatedAt);

  if (isNaN(currentTicketUpdatedAtDate.getTime()) || isNaN(newTicketUpdatedAtDate.getTime())) {
    return false;
  }

  return currentTicketUpdatedAtDate > newTicketUpdatedAtDate;
};

function truncateQueue(queue, useFifoOrder) {
  const { allTickets } = queue;

  if (allTickets.length <= CONVERSATION_QUERY_LIMIT) {
    return;
  }

  const overflow = allTickets.length - CONVERSATION_QUERY_LIMIT;

  if (useFifoOrder) {
    const removed = allTickets.splice(0, overflow);
    removed.forEach((id) => delete queue[id]);
  } else {
    const removed = allTickets.splice(CONVERSATION_QUERY_LIMIT, overflow);
    removed.forEach((id) => delete queue[id]);
  }
}

export const INITIAL_STATE = {
  nominatedTickets: { allTickets: [] },
  assignedTickets: { allTickets: [] },
  closedTickets: { allTickets: [] }, // TODO: Remove
  allTickets: { allTickets: [] },
  awaitingWebChatNominationCount: null,
  awaitingEmailMessageNominationCount: null,
  historicalTickets: { allTickets: [] },
  reviewTickets: { allTickets: [] },
};

const tickets = (state = INITIAL_STATE, action, globalState) => {
  let assignedTickets, nominatedTickets, allTickets, queue;
  // TODO: avoid copying state on every action
  if (state.assignedTickets !== undefined && state.nominatedTickets !== undefined) {
    assignedTickets = Object.assign({}, state.assignedTickets, {
      allTickets: Object.assign([], state.assignedTickets.allTickets),
    });
    nominatedTickets = Object.assign({}, state.nominatedTickets, {
      allTickets: Object.assign([], state.nominatedTickets.allTickets),
    });
    allTickets = Object.assign({}, state.allTickets, {
      allTickets: Object.assign([], state.allTickets.allTickets),
    });
    const notNominatedTickets =
      action.queueName === ASSIGNED || action.queueName === LIVE_CHAT ? assignedTickets : allTickets;
    queue = action.queueName === NOMINATED ? nominatedTickets : notNominatedTickets;
  }
  switch (action.type) {
    case MOVE_TICKET:
    case MOVE_TICKET_FROM_ALL_TO_ASSIGNED:
      // see what the source queue is based off the action type
      let sourceQueue = action.type === MOVE_TICKET_FROM_ALL_TO_ASSIGNED ? allTickets : nominatedTickets;

      // if we have the conversation already in assigned, just return the original state
      if (assignedTickets[action.conversationObjectId]) {
        return state;
      }

      // add the ticket to assigned
      // TODO: Insert ticket at position determined by firstMessageArrivedAt
      assignedTickets.allTickets.unshift(action.conversationObjectId);
      assignedTickets[action.conversationObjectId] = action.ticket;

      // remove the ticket from the source queue if it is there
      let indexInSource = sourceQueue.allTickets.indexOf(action.conversationObjectId);
      if (indexInSource > -1) {
        sourceQueue.allTickets.splice(indexInSource, 1);
        delete sourceQueue[action.conversationObjectId];
      }

      return {
        ...state,
        nominatedTickets: nominatedTickets,
        assignedTickets: assignedTickets,
        allTickets: allTickets,
      };

    case ADD_TICKET: {
      const ticket = action.ticket;

      if (queue[action.ticket.objectId] !== undefined) {
        return state;
      }

      // eslint-disable-next-line react-hooks/rules-of-hooks
      const isFifo = useFifoOrderingForTab(globalState, action.queueName);

      // if the tab is full, just return the original state since the new item would go on the next page
      if (isFifo && isTabFull(globalState, action.queueName)) {
        return state;
      }

      // creates new allTickets ordered array of ids
      const getUpdatedAllTickets = (ticketId) => {
        // check if this tab needs fifo or lifo
        // eslint-disable-next-line
        if (isFifo) {
          return [...queue.allTickets, ticketId];
        }

        return [ticketId, ...queue.allTickets];
      };

      switch (action.queueName) {
        case NOMINATED: {
          const newNominatedQueue = {
            ...state.nominatedTickets,
            allTickets: getUpdatedAllTickets(action.ticket.objectId),
            [action.ticket.objectId]: {
              ...processNominatedOrReviewTicket(ticket, action.nominationExpiresAt),
              lastCustomerMessageBody: action.lastCustomerMessageBody,
            },
          };

          truncateQueue(newNominatedQueue, isFifo);

          return {
            ...state,
            nominatedTickets: newNominatedQueue,
          };
        }

        case ASSIGNED:
        case LIVE_CHAT: {
          const processedAssignedTicket = processAssignedTicket(ticket);

          const newAssignedQueue = {
            ...state.assignedTickets,
            allTickets: getUpdatedAllTickets(action.ticket.objectId),
            [action.ticket.objectId]: {
              ...processedAssignedTicket,
              // assume ticket has unviewed messages since it's new
              unviewedMessageIds: [processedAssignedTicket.lastMessageId],
            },
          };

          truncateQueue(newAssignedQueue, isFifo);

          return {
            ...state,
            assignedTickets: newAssignedQueue,
          };
        }

        case ALL: {
          const processedTicket = processAssignedTicket(ticket);

          const newAllQueue = {
            ...state.allTickets,
            allTickets: getUpdatedAllTickets(action.ticket.objectId),
            [action.ticket.objectId]: processedTicket,
          };

          truncateQueue(newAllQueue, isFifo);

          return {
            ...state,
            allTickets: newAllQueue,
          };
        }

        case REVIEW: {
          const processedReviewTicket = processNominatedOrReviewTicket(action.ticket);
          const ticketId = action.ticket.objectId;

          const newReviewQueue = {
            ...state.reviewTickets,
            allTickets: [...state.reviewTickets.allTickets, ticketId],
            [action.ticket.objectId]: processedReviewTicket,
          };

          truncateQueue(newReviewQueue, isFifo);

          return {
            ...state,
            reviewTickets: newReviewQueue,
          };
        }
        default:
          throw new Error(`unknown queue ${action.queueName}`);
      }
    }

    case UPDATE_REVIEW_TICKET: {
      const { ticketId, ticket: reviewTicket } = action;
      const currentTicket = state.reviewTickets[ticketId];

      if (!currentTicket || isStaleTicketUpdate(reviewTicket, currentTicket)) {
        return state;
      }

      return dotProp.set(state, ['reviewTickets', ticketId], reviewTicket);
    }

    case UPDATE_TICKET:
      switch (action.queueName) {
        case NOMINATED: {
          // only update if exists in queue
          const currentTicket = state.nominatedTickets[action.ticket.ticketId];

          if (!currentTicket || isStaleTicketUpdate(action.ticket, currentTicket)) {
            return state;
          }

          return dotProp.set(state, ['nominatedTickets', action.ticket.ticketId], action.ticket);
        }
        case ASSIGNED: {
          const currentTicket = state.assignedTickets[action.ticket.ticketId];
          if (!currentTicket) {
            console.warn(`Ticket not found in assignedTickets: ${action.ticket.ticketId}`);

            return state;
          }

          if (isStaleTicketUpdate(action.ticket, currentTicket)) {
            return state;
          }

          const didUnreadCountIncrease = action.ticket.unreadCount > currentTicket.unreadCount;

          const shouldAddUnviewedMessage =
            didUnreadCountIncrease && !isTicketBeingViewed(globalState, action.ticket.ticketId);

          const unviewedMessageIds = shouldAddUnviewedMessage
            ? [...currentTicket.unviewedMessageIds, action.ticket.lastMessageId]
            : currentTicket.unviewedMessageIds;

          const newTicket = {
            ...action.ticket,
            // carry over unviewed message ids from previous ticket
            unviewedMessageIds,
          };

          return dotProp.set(state, ['assignedTickets', action.ticket.ticketId], newTicket);
        }
        case ALL: {
          const currentTicket = state.allTickets[action.ticket.ticketId];

          if (!currentTicket || isStaleTicketUpdate(action.ticket, currentTicket)) {
            return state;
          }

          const newAllQueue = {
            ...state.allTickets,
            [action.ticket.ticketId]: action.ticket,
          };
          // get the sorting params
          const { useFifoOrder, timeSortField } = getSortParamsForTab(globalState, action.queueName);

          // Forward to Experts queue sorting must update with new messages
          const sortedTickets = sortTickets(extractTicketsArray(newAllQueue), timeSortField, useFifoOrder);

          return {
            ...state,
            allTickets: {
              ...newAllQueue,
              allTickets: sortedTickets.map(({ ticketId }) => ticketId),
            },
          };
        }
        default:
          throw new Error(`unknown queue name ${action.queueName}`);
      }

    case UPDATE_TICKET_ORDER: {
      const { ticketId, orderNumber, orderingSystem } = action;

      const ticket = queue[ticketId];
      ticket['orderingSystem'] = orderingSystem;
      ticket[`${orderingSystem}OrderNumber`] = orderNumber;

      return {
        ...state,
        nominatedTickets: nominatedTickets,
        assignedTickets: assignedTickets,
        allTickets: allTickets,
      };
    }

    case ADD_PREVIOUS_TICKETS: {
      /** (apoorvyadav1|SPD-11740|2025-03-04): For displaying previous conversations in
       * a review ticket to the partner protectors, we add the previous conversations
       * of the ticket in both reviewTicketsQueue queue and queue. Adding in reviewTicketsQueue
       * ensures that the previous conversations are displayed in a review ticket.
       */
      if (!action.ticketId || !action.previousConversations) {
        return state;
      }

      const ticket = queue[action.ticketId];
      if (ticket) {
        ticket['previousConversations'] = action.previousConversations;
      }

      const { allTickets: allReviewTicketsIds, ...reviewTicketsQueue } = state.reviewTickets;
      const updatedReviewTicketsQueue = { ...reviewTicketsQueue };
      const reviewTicket = updatedReviewTicketsQueue[action.ticketId];
      if (reviewTicket) {
        updatedReviewTicketsQueue[action.ticketId] = {
          ...reviewTicket,
          previousConversations: action.previousConversations,
        };
      }

      return {
        ...state,
        nominatedTickets: nominatedTickets,
        assignedTickets: assignedTickets,
        allTickets: allTickets,
        reviewTickets: {
          ...state.reviewTickets,
          ...updatedReviewTicketsQueue,
        },
      };
    }

    case REMOVE_REVIEW_TICKET: {
      const { allTickets: allReviewTicketsIds, ...reviewTicketsQueue } = state.reviewTickets;
      delete reviewTicketsQueue[action.ticketId];

      return Object.assign({}, state, {
        reviewTickets: {
          allTickets: allReviewTicketsIds.filter((id) => id !== action.ticketId),
          ...reviewTicketsQueue,
        },
      });
    }

    case REMOVE_TICKET:
      if (queue[action.ticketId] === undefined) {
        return state;
      }

      delete queue[action.ticketId];
      queue.allTickets.splice(queue.allTickets.indexOf(action.ticketId), 1);

      return {
        ...state,
        nominatedTickets: action.queueName === NOMINATED ? queue : nominatedTickets,
        assignedTickets: action.queueName === ASSIGNED ? queue : assignedTickets,
        allTickets: action.queueName === ALL ? queue : allTickets,
      };

    case HYDRATE_ALL_TICKETS:
      allTickets = Object.assign({}, action.tickets, {
        allTickets: Object.assign([], action.tickets.allTickets),
      });

      return {
        ...state,
        nominatedTickets: nominatedTickets,
        assignedTickets: assignedTickets,
        allTickets: allTickets,
      };

    case HYDRATE_NOMINATED_TICKETS:
      return {
        ...state,
        nominatedTickets: action.nominatedTickets,
      };

    case HYDRATE_REVIEW_TICKETS:
      return {
        ...state,
        reviewTickets: action.reviewTickets,
      };

    case HYDRATE_ASSIGNED_TICKETS:
      return {
        ...state,
        assignedTickets: action.assignedTickets,
      };

    case HYDRATE_HISTORICAL_TICKETS:
      return {
        ...state,
        historicalTickets: action.historicalTickets,
      };

    case TICKETS_SET_TO_EMPTY:
      return INITIAL_STATE;

    case UPDATE_AWAITING_WEB_CHAT_NOMINATION_COUNT:
      return {
        ...state,
        awaitingWebChatNominationCount: action.count,
      };

    case UPDATE_AWAITING_EMAIL_MESSAGE_NOMINATION_COUNT:
      return {
        ...state,
        awaitingEmailMessageNominationCount: action.count,
      };

    case SELECT_ASSIGNED_TICKET:
    case SELECT_LIVE_CHAT_TICKET:
      return dotProp.set(state, ['assignedTickets', action.selectedTicketId, 'unviewedMessageIds'], []);

    default:
      return state;
  }
};

export default tickets;
